import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import MessageContent from './message/MessageContent';
import MapView from './maps/MapView';
import { FeedbackDialog, ExplanationDialog } from './dialogs';
import { useChartData } from '../../hooks/useChartData';
import { useMapData } from '../../hooks/useMapData';
import { cn } from '../../lib/utils';
import {
  SqlSection,
  JsonSection,
  TableSection,
  ChartSection,
  InsightsSection,
  InvestigationsSection,
  ControlButtons
} from './sections';

const getFeedbackStatus = (response) => {
  const feedback = JSON.parse(localStorage.getItem('userFeedback') || '[]');
  return feedback.find(f => JSON.stringify(f.response) === JSON.stringify(response));
};

const ChatMessage = ({ type, content, rawResponse, isLoading }) => {
  const [defaultOpenSection, setDefaultOpenSection] = useState(() => localStorage.getItem('defaultOpenSection') || 'none');
  const [showJson, setShowJson] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [showSql, setShowSql] = useState(false);
  const [showChart, setShowChart] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  const [showInvestigations, setShowInvestigations] = useState(false);
  const [showSqlResponse, setShowSqlResponse] = useState(() => localStorage.getItem('showSqlResponse') === 'true');
  const [showJsonResponse, setShowJsonResponse] = useState(() => localStorage.getItem('showJsonResponse') === 'true');
  const [showTableResponse, setShowTableResponse] = useState(() => localStorage.getItem('showTableResponse') === 'true');
  const [isCopied, setIsCopied] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackType, setFeedbackType] = useState('negative');
  const [existingFeedback, setExistingFeedback] = useState(() => getFeedbackStatus(rawResponse));
  const [showVisualizeData, setShowVisualizeData] = useState(localStorage.getItem('showVisualizeData') === 'true');
  const [showExplanationPopup, setShowExplanationPopup] = useState(false);
  const [showInsightsResponse, setShowInsightsResponse] = useState(() => localStorage.getItem('showInsightsResponse') === 'true');
  const [showInvestigationsResponse, setShowInvestigationsResponse] = useState(() => localStorage.getItem('showInvestigationsResponse') === 'true');
  
  // Update settings when localStorage changes (cross-tab)
  useEffect(() => {
    const handleStorageChange = () => {
      setShowVisualizeData(localStorage.getItem('showVisualizeData') === 'true');
      setShowSqlResponse(localStorage.getItem('showSqlResponse') === 'true');
      setShowJsonResponse(localStorage.getItem('showJsonResponse') === 'true');
      setShowTableResponse(localStorage.getItem('showTableResponse') === 'true');
      setShowInsightsResponse(localStorage.getItem('showInsightsResponse') === 'true');
      setShowInvestigationsResponse(localStorage.getItem('showInvestigationsResponse') === 'true');
      setDefaultOpenSection(localStorage.getItem('defaultOpenSection') || 'none');
    };

    const handleSettingsChange = (event) => {
      const { key, value } = event.detail;
      const setters = {
        showVisualizeData: setShowVisualizeData,
        showSqlResponse: setShowSqlResponse,
        showJsonResponse: setShowJsonResponse,
        showTableResponse: setShowTableResponse,
        showInsightsResponse: setShowInsightsResponse,
        showInvestigationsResponse: setShowInvestigationsResponse,
        defaultOpenSection: setDefaultOpenSection,
      };
      if (setters[key]) setters[key](value);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('settingsChanged', handleSettingsChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('settingsChanged', handleSettingsChange);
    };
  }, []);

  const hasTableResults = rawResponse?.results?.rows && Array.isArray(rawResponse.results.rows);
  const hasSqlQuery = showSqlResponse && rawResponse && 'sql' in rawResponse;
  // Add flag for JSON and Table availability based on localStorage settings
  const hasJsonResponse = showJsonResponse && rawResponse;
  const hasTableResponse = showTableResponse && hasTableResults;

  // REFACTOR: Use custom hooks for chart and map data processing
  const { chartType, hasChartData, chartData, chartOptions } = useChartData(
    rawResponse,
    hasTableResults,
    showVisualizeData
  );

  const mapData = useMapData(rawResponse, chartType);
  
  // Track chart data detection
  useEffect(() => {
    // Chart data check tracking
  }, [rawResponse, isLoading, hasTableResults, hasChartData, chartType]);
  
  // Insights and investigations come exclusively from the interpret API
  const hasInsights = showInsightsResponse && (rawResponse?.interpretation?.insights?.length > 0 || rawResponse?.insights?.length > 0);
  const hasInvestigations = showInvestigationsResponse && (rawResponse?.interpretation?.suggested_investigations?.length > 0 || 
                           rawResponse?.suggested_investigations?.length > 0);
  const isDebugMode = localStorage.getItem('debug') === 'true';
  const hasExplanation = isDebugMode && !!rawResponse?.explanation;
                           
  // Track insights and investigations
  useEffect(() => {
    // Insights and investigations check tracking
  }, [rawResponse, isLoading, hasInsights, hasInvestigations, showInsightsResponse, showInvestigationsResponse]);

  // Track showChart changes
  useEffect(() => {
    // showChart state tracking
  }, [showChart]);

  // Track analysis flag for debugging
  useEffect(() => {
    if (isLoading && type === 'question') {
      // Analysis in progress tracking
    }
  }, [isLoading, type]);

  // REFACTOR: Chart rendering now uses data from hooks
  const renderChart = () => {
    if (chartType === null || !chartData) {
      return null;
    }

    if (chartType.toLowerCase() === 'map') {
      return <MapView data={mapData} />;
    }

    // For other chart types, use MessageContent component
    return (
      <div className="w-full h-[400px]">
        <MessageContent
          content={{
            chartType,
            data: chartData,
            options: chartOptions
          }}
          type="chart"
        />
      </div>
    );
  };

  const handleCopySQL = async () => {
    try {
      await navigator.clipboard.writeText(rawResponse.sql);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000); // Reset after 2 seconds
    } catch (err) {
      console.error('Failed to copy SQL:', err);
    }
  };

  const getQuestionText = useCallback(() => {
    // If this is a question message, use its content
    if (type === 'question') {
      return content;
    } 
    
    // For answer messages, try to get the question from rawResponse
    if (rawResponse) {
      // Check if question is directly in rawResponse
      if (rawResponse.question) {
        return rawResponse.question;
      }
      
      // Check if there's a message property that might contain the question
      if (rawResponse.message && typeof rawResponse.message === 'string') {
        return rawResponse.message;
      }
    }
    
    // Default fallback
    return 'No question available';
  }, [type, content, rawResponse]);

  const handleFeedbackSubmit = (newFeedback) => {
    setExistingFeedback(newFeedback);
  };

  useEffect(() => {
    // Dispatch response received event when message is complete
    if (!isLoading && type === 'answer') {
      window.dispatchEvent(new CustomEvent('chatResponseReceived'));
      
      // Dispatch an event to focus the input field when a message is received
      window.dispatchEvent(new CustomEvent('focusChatInput'));
    }
  }, [isLoading, type]);

  useEffect(() => {
    setExistingFeedback(getFeedbackStatus(rawResponse));
  }, [rawResponse]);

  // Smart auto-expand: determine which section to open based on available data
  const determineSmartSection = useCallback(() => {
    // Priority 1: Charts/Maps (most visual impact)
    if (hasChartData && showVisualizeData) {
      return 'chart';
    }
    // Priority 2: Insights (valuable analysis)
    if (hasInsights) {
      return 'insights';
    }
    // Priority 3: Investigations (suggested next steps)
    if (hasInvestigations) {
      return 'investigations';
    }
    // Priority 4: Table (data results)
    if (hasTableResponse) {
      return 'table';
    }
    // Priority 5: SQL (for technical users)
    if (hasSqlQuery) {
      return 'sql';
    }
    return 'none';
  }, [hasChartData, showVisualizeData, hasInsights, hasInvestigations, hasTableResponse, hasSqlQuery]);

  // Set default open section only when data is fully loaded (not during loading and not waiting for interpretation)
  const hasAutoExpanded = useRef(false);
  useEffect(() => {
    if (hasAutoExpanded.current) return;
    if (!isLoading && (!rawResponse?.waitingForInterpretation || rawResponse?.interpretation) && type !== 'question') {
      hasAutoExpanded.current = true;
      let sectionToOpen = 'none';

      // If user has set a specific default section, use that
      if (defaultOpenSection !== 'none') {
        sectionToOpen = defaultOpenSection;
        setShowJson(defaultOpenSection === 'json');
        setShowTable(defaultOpenSection === 'table');
        setShowSql(defaultOpenSection === 'sql');
        setShowChart(defaultOpenSection === 'chart');
        setShowInsights(defaultOpenSection === 'insights');
        setShowInvestigations(defaultOpenSection === 'investigations');
      } else {
        // Otherwise, use smart auto-expand
        const smartSection = determineSmartSection();
        sectionToOpen = smartSection;
        setShowJson(smartSection === 'json');
        setShowTable(smartSection === 'table');
        setShowSql(smartSection === 'sql');
        setShowChart(smartSection === 'chart');
        setShowInsights(smartSection === 'insights');
        setShowInvestigations(smartSection === 'investigations');
      }

      // Trigger scroll after section auto-expands
      if (sectionToOpen !== 'none') {
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('chatSectionExpanded'));
        }, 150);
      }
    }
  }, [defaultOpenSection, isLoading, rawResponse, type, determineSmartSection]);

  // Get insights and investigations from the response
  const getInsights = () => {
    return rawResponse?.interpretation?.insights || rawResponse?.insights || [];
  };

  const getInvestigations = () => {
    return rawResponse?.interpretation?.suggested_investigations || rawResponse?.suggested_investigations || [];
  };

  // --- Add function to toggle explanation popup ---
  const toggleExplanationPopup = () => {
    setShowExplanationPopup(!showExplanationPopup);
  };
  // --- End added function ---

  return (
    <div
      className="w-full animate-fadeIn"
      data-message-type={type}
    >
      <div className="w-full px-4 py-3">
        {type === 'question' ? (
          <div className="flex justify-end">
            <div className={cn(
              "max-w-2xl lg:max-w-4xl px-6 py-4 rounded-2xl shadow-sm",
              "bg-blue-50 border border-blue-200 text-blue-900"
            )}>
              <p className="whitespace-pre-wrap leading-relaxed">{content}</p>
              <div className="flex justify-end mt-1">
                <span className="text-xs text-blue-600">Just now</span>
              </div>
            </div>
          </div>
        )
         : (
          <div className="flex justify-start">
            <div className={cn(
              "max-w-2xl lg:max-w-4xl px-6 py-4 rounded-2xl shadow-md hover:shadow-lg transition-shadow",
              "bg-white border border-gray-200 text-gray-800"
            )}>
              {/* AI Avatar Badge */}
              <div className="flex items-start gap-3 mb-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-semibold">
                  AI
                </div>
                <div className="flex-1 min-w-0">
                  {// Only show content if it's not a bot message repeating the question
                  (type !== 'bot' || (type === 'bot' && rawResponse?.message !== content)) && content && (
                    <div className="prose prose-sm max-w-none prose-headings:text-gray-800 prose-p:text-gray-700 prose-strong:text-gray-800 prose-code:text-blue-600 prose-code:bg-blue-50 prose-code:px-1 prose-code:py-0.5 prose-code:rounded leading-relaxed">
                      <ReactMarkdown>{content}</ReactMarkdown>
                    </div>
                  )}

                  {(rawResponse?.interpretation?.summary || rawResponse?.summary) && (
                    <div className="prose prose-sm max-w-none prose-headings:text-gray-800 prose-p:text-gray-700 prose-strong:text-gray-800 mt-2 leading-relaxed">
                      <ReactMarkdown>{rawResponse?.interpretation?.summary || rawResponse?.summary}</ReactMarkdown>
                    </div>
                  )}
                </div>
              </div>

              {(!isLoading && (!rawResponse?.waitingForInterpretation || rawResponse?.interpretation)) && (
                <div className={`mt-3 pt-3 ${(content || hasTableResults || hasSqlQuery) ? 'border-t border-gray-100' : ''}`}>
                  <ControlButtons
                    hasSqlQuery={hasSqlQuery}
                    showSql={showSql}
                    setShowSql={setShowSql}
                    showJson={showJson}
                    setShowJson={setShowJson}
                    hasTableResults={hasTableResults}
                    showTable={showTable}
                    setShowTable={setShowTable}
                    hasChartData={hasChartData}
                    showVisualizeData={showVisualizeData}
                    showChart={showChart}
                    setShowChart={setShowChart}
                    hasInsights={hasInsights}
                    showInsights={showInsights}
                    setShowInsights={setShowInsights}
                    hasInvestigations={hasInvestigations}
                    showInvestigations={showInvestigations}
                    setShowInvestigations={setShowInvestigations}
                    existingFeedback={existingFeedback}
                    setFeedbackType={setFeedbackType}
                    setShowFeedback={setShowFeedback}
                    rawResponse={rawResponse}
                    hasExplanation={hasExplanation}
                    onToggleExplanation={toggleExplanationPopup}
                  />
                </div>
              )}

              <div className="mt-2">
                {showSql && hasSqlQuery && rawResponse?.sql && (
                  <SqlSection sql={rawResponse.sql} />
                )}

                {showJson && hasJsonResponse && (
                  <JsonSection data={rawResponse} content={content} />
                )}

                {showTable && hasTableResults && (
                  <TableSection results={rawResponse.results} />
                )}

                {showChart && hasChartData && showVisualizeData && (
                  <ChartSection rawResponse={rawResponse} />
                )}

                {showInsights && hasInsights && (
                  <InsightsSection insights={getInsights()} />
                )}

                {showInvestigations && hasInvestigations && (
                  <InvestigationsSection investigations={getInvestigations()} />
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* REFACTOR: Use extracted dialog components */}
      <FeedbackDialog
        isOpen={showFeedback}
        onClose={() => setShowFeedback(false)}
        feedbackType={feedbackType}
        rawResponse={rawResponse}
        onSubmit={handleFeedbackSubmit}
        getQuestionText={getQuestionText}
      />

      <ExplanationDialog
        isOpen={showExplanationPopup}
        onClose={toggleExplanationPopup}
        explanation={rawResponse?.explanation}
      />
    </div>
  );
};

ChatMessage.propTypes = {
  type: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
  rawResponse: PropTypes.shape({
    message: PropTypes.string,
    sql: PropTypes.string,
    results: PropTypes.shape({
      rows: PropTypes.array,
      columns: PropTypes.array,
      rowCount: PropTypes.number
    }),
    interpretation: PropTypes.shape({
      summary: PropTypes.string,
      suggestedChart: PropTypes.string,
      dimensions: PropTypes.object,
      map: PropTypes.object,
      insights: PropTypes.array,
      suggested_investigations: PropTypes.array
    }),
    summary: PropTypes.string,
    suggestedChart: PropTypes.string,
    dimensions: PropTypes.object,
    map: PropTypes.object,
    insights: PropTypes.array,
    suggested_investigations: PropTypes.array,
    explanation: PropTypes.string,
    waitingForInterpretation: PropTypes.bool
  }), // rawResponse can be undefined for question messages
  isLoading: PropTypes.bool.isRequired
};

ChatMessage.defaultProps = {
  rawResponse: null
};

export default ChatMessage;